import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { untilDestroyed } from '@ngneat/until-destroy';

import { ConnectorConfiguration } from '@blaze-shared/connectors-domain';
import {
  CopyConnectorConfigEnvButtonComponent,
  DialogModule,
  EnvironmentIndicatorComponent,
} from '@blaze-shared/shared-feature';
import { ButtonBarComponent, PanelComponent, PanelRef } from '@blaze-shared/shared-ui';

import { BaseConnectorDialogComponent } from '../../__base-connector-dialog/base-connector-dialog.component';
import { SecretInputComponent } from '../../secret-input/secret-input.component';
import { prepareConnectorConfiguration } from '../../utils';
import { WebhookIdInputComponent } from '../../webhook-id-input/webhook-id-input.component';

import { CalConfiguration, ConnectorForm } from './cal-connector-dialog.models';

@Component({
  selector: 'blaze-cal-connector-dialog',
  imports: [
    AsyncPipe,
    ButtonBarComponent,
    CopyConnectorConfigEnvButtonComponent,
    DialogModule,
    EnvironmentIndicatorComponent,
    FormsModule,
    MatIconModule,
    MatSlideToggleModule,
    PanelComponent,
    ReactiveFormsModule,
    SecretInputComponent,
    WebhookIdInputComponent,
  ],
  templateUrl: './cal-connector-dialog.component.html',
  styleUrls: ['./cal-connector-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'data-connector-dialog' },
})
export class CalConnectorDialogComponent
  extends BaseConnectorDialogComponent<CalConfiguration>
  implements OnInit
{
  private readonly panelRef = inject(PanelRef);

  protected readonly webhookBaseUrl = '/cal/webhook';

  serviceName = 'Cal.com';
  connectorForm: FormGroup<ConnectorForm>;
  isLoading = false;

  constructor() {
    super();
    this.connectorForm = new FormGroup({
      connectorName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      apiKey: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      useWebhook: new FormControl(false),
      webhookId: new FormControl(''),
      secret: new FormControl(''),
    });

    this.connectorForm.controls.useWebhook.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((useWebhook) => {
        if (!useWebhook) {
          this.connectorForm.controls.secret.clearValidators();
          this.connectorForm.patchValue({
            webhookId: '',
            secret: '',
          });
          this.connectorForm.controls.secret.updateValueAndValidity();
          return;
        }

        const configuration = this.getConfiguration(this.connector);
        const webhookId = configuration.id;
        this.connectorForm.patchValue({
          webhookId,
          secret: configuration.secret ?? '',
        });
        this.connectorForm.controls.secret.addValidators([Validators.required]);
        this.connectorForm.controls.secret.updateValueAndValidity();
      });
  }

  ngOnInit(): void {
    if (this.connector) {
      this.fillForm(this.getConfiguration(this.connector));
    }
  }

  saveConnector(): void {
    if (this.connectorForm.invalid) {
      return;
    }

    const { connectorName, apiKey, webhookId, secret } = this.connectorForm.value;

    if (!connectorName || !apiKey) {
      return;
    }

    const connectorConfig = prepareConnectorConfiguration(
      connectorName,
      this.template?.id,
      this.accountId,
      this.connector,
      {
        apiKey,
        secret: secret ?? '',
        id: webhookId ?? '',
      },
    );

    this.isLoading = true;
    this.saveOrUpdateConnector(connectorConfig, this.connectorId)
      .pipe(untilDestroyed(this))
      .subscribe((connector) => {
        if (connector) {
          this.panelRef.close();
        }
        this.isLoading = false;
        this.cdr.markForCheck();
      });
  }

  getConfiguration(connector?: ConnectorConfiguration): CalConfiguration {
    const connectorId = connector?.id ?? '';
    const connectorName = connector?.name ?? '';
    const configuration = connector?.configuration ?? {};

    const apiKey = String(configuration?.apiKey ?? '');
    const id = String(configuration?.id ?? '');
    const secret = String(configuration?.secret ?? '');

    return {
      connectorId,
      connectorName,
      apiKey,
      id,
      secret,
    };
  }

  fillForm(configuration: CalConfiguration): void {
    const { connectorId, connectorName, apiKey, id, secret } = configuration;

    const useWebhook = !!id;

    this.connectorId = connectorId;

    this.connectorForm.patchValue(
      {
        connectorName,
        apiKey,
        useWebhook,
        webhookId: id,
        secret,
      },
      { emitEvent: false },
    );
  }
}
